import React, {useMemo, useRef, useState} from "react";
import {classNames} from "primereact/utils";
import {ContextMenu} from "primereact/contextmenu";
import {MenuItem} from "primereact/menuitem";
import {IConversationMessageMetadataModel, IConversationMessageModel,} from "../../Services/ConversationService";
import "./Conversation.scss"
import {useTranslation} from "react-i18next";
import {DateText} from "../../Util/timeUtil";
import SecurityUserResponse from "../../Models/Authentication/SecurityUserResponse";
import DOMPurify from "dompurify";
import {ConversationMessageService} from "../../Services/ConversationMessageService";
import {toast} from "react-toastify";
import {Tooltip} from "primereact/tooltip";
import {readAskAiAccess} from "../../Util/permissionUtil";
import AppDialog from "../Dialogs/AppDialog/AppDialog";
import {KNOWLEDGE_ARTICLE_HREF_PATTERN} from "../../Pages/KnowledgeBase/knowledgeBaseConstants";
import {htmlToConversationPlainText} from "./conversationPdfExport";

const COPYABLE_ASK_AI_CODES = ["ASKAIQSTN", "ASKAIRSPN"];

interface ConversationBubbleProps {
	type: "MESSAGING" | "ASKAI" | "DATE" | undefined,
	conversationMessage: IConversationMessageModel,
	user?: SecurityUserResponse,
	groupByDate?: boolean,
	selectionAllowed?: boolean,
	onMetadataUpdate?: (conversationMessageUuid: string, metadata: IConversationMessageMetadataModel[] | undefined) => void,
}

/** One step of the Ask AI pipeline's timing breakdown — see backend {@code PipelineTimingCapture}. */
interface ITimingStep {
	name: string,
	startOffsetMillis: number,
	durationMillis: number,
}

/** Gemini token usage for one Ask AI turn — see backend {@code TokenUsageCapture}. */
interface ITokenUsageCall {
	name: string,
	inputTokens: number,
	outputTokens: number,
	totalTokens: number,
}

interface ITokenUsageBreakdown {
	calls: ITokenUsageCall[],
	inputTokens: number,
	outputTokens: number,
	totalTokens: number,
}

/** Ask AI may link kennisbank articles; invented hrefs (example.com slugs) are dropped. */
const ASK_AI_HTML_PURIFY = {
	ALLOWED_ATTR: ["href"],
	ALLOWED_URI_REGEXP: KNOWLEDGE_ARTICLE_HREF_PATTERN,
};

const sanitizeConversationHtml = (
	html: string | undefined,
	conversationType: ConversationBubbleProps["type"],
): string => {
	if (!html) {
		return "";
	}
	if (conversationType === "ASKAI") {
		return DOMPurify.sanitize(html, ASK_AI_HTML_PURIFY);
	}
	return DOMPurify.sanitize(html);
};

const ConversationBubble: React.FC<ConversationBubbleProps> = ({
	                                                               type,
	                                                               conversationMessage,
	                                                               user,
	                                                               groupByDate = false,
	                                                               selectionAllowed = true,
	                                                               onMetadataUpdate
                                                               }: ConversationBubbleProps) => {

	const {t} = useTranslation();

	const {canViewDebugInfo} = useMemo(() => readAskAiAccess(), []);
	const contextMenuRef = useRef<ContextMenu>(null);
	const [debugDialogVisible, setDebugDialogVisible] = useState(false);

	const rotatingTextMessages = [
		t('conversation.generating.understanding-question'),
		t('conversation.generating.retrieving-schema'),
		t('conversation.generating.determining-useful-columns'),
		t('conversation.generating.retrieving-data'),
		t('conversation.generating.analyzing-data'),
		t('conversation.generating.generating-response'),
	];
	const [rotatingText, setRotatingText] = React.useState<string>(rotatingTextMessages[0]);

	React.useEffect(() => {
		if (conversationMessage.conversationMessageType?.code === "ASKAIGNRN") {
			let currentIndex = 0;

			setRotatingText(rotatingTextMessages[currentIndex]);

			const interval = setInterval(() => {
				currentIndex = (currentIndex + 1) % rotatingTextMessages.length;
				setRotatingText(rotatingTextMessages[currentIndex]);
			}, 3000);

			return () => clearInterval(interval);
		} else {
			setRotatingText(t('conversation.generating-response'));
		}
	}, [conversationMessage?.conversationMessageType?.code ?? '']);

	const rateConversationMessage = (rating: number) => {
		if (rating === retrieveRating()) return;
		new ConversationMessageService().postMessage(conversationMessage.uuid, {rating: rating}).then((response) => {
			let newMetadata = conversationMessage.metadata;
			if (!newMetadata) {
				newMetadata = [{
					key: "RATING",
					value: rating.toString(),
				} as IConversationMessageMetadataModel];
			} else {
				let ratingMetadata: IConversationMessageMetadataModel | undefined = newMetadata.find((meta) => meta.key === 'RATING');
				if (!ratingMetadata) {
					newMetadata.push({
							key: "RATING",
							value: rating.toString(),
						} as IConversationMessageMetadataModel)
				} else {
					ratingMetadata.value = rating.toString();
				}
			}
			onMetadataUpdate && onMetadataUpdate(conversationMessage.uuid, newMetadata);
		}).catch((ex) => {
			toast.error(t('common.failed'));
		})
	}

	const retrieveRating = () : number | undefined => {
		if (conversationMessage && conversationMessage.metadata) {
			let ratingMetadata: string | undefined = conversationMessage.metadata?.find(metadata => metadata.key === 'RATING')?.value;
			if (ratingMetadata !== undefined) {
				let ratingInteger = parseInt(ratingMetadata);
				return ratingInteger;
			} else {
				return undefined;
			}
		}
		return undefined;
	}

	const findMetadataValue = (key: string): string | undefined => {
		return conversationMessage.metadata?.find((metadata) => metadata.key === key)?.value;
	}

	const parseTimingBreakdown = (value: string | undefined): ITimingStep[] => {
		if (!value) {
			return [];
		}
		try {
			const parsed = JSON.parse(value);
			if (!Array.isArray(parsed)) {
				return [];
			}
			return [...parsed]
				.filter((step): step is ITimingStep => !!step && typeof step.name === 'string')
				.sort((a, b) => (a.startOffsetMillis ?? 0) - (b.startOffsetMillis ?? 0));
		} catch {
			return [];
		}
	}

	const formatStepDuration = (millis: number): string => {
		return millis >= 1000 ? `${(millis / 1000).toFixed(1)}s` : `${millis}ms`;
	}

	const isToolTimingStep = (step: ITimingStep): boolean => {
		return /Tool/i.test(step.name);
	}

	const renderTimingRows = (steps: ITimingStep[], totalMillis: number, rowClassName?: string) => {
		return steps.map((step, index) => {
			const leftPct = (step.startOffsetMillis / totalMillis) * 100;
			const widthPct = Math.max((step.durationMillis / totalMillis) * 100, 1);
			return (
				<div
					className={classNames("bubble-timing-timeline__row", rowClassName)}
					key={`${step.name}-${step.startOffsetMillis}-${index}`}
					title={`${step.name}: ${formatStepDuration(step.durationMillis)}`}
				>
					<span className="bubble-timing-timeline__label">{step.name}</span>
					<span className="bubble-timing-timeline__track">
						<span
							className="bubble-timing-timeline__bar"
							style={{left: `${leftPct}%`, width: `${widthPct}%`}}
						/>
					</span>
					<span className="bubble-timing-timeline__duration">{formatStepDuration(step.durationMillis)}</span>
				</div>
			);
		});
	}

	const renderTimingTimeline = (steps: ITimingStep[]) => {
		if (steps.length === 0) {
			return undefined;
		}
		const totalMillis = Math.max(...steps.map((step) => step.startOffsetMillis + step.durationMillis), 1);
		const toolSteps = steps.filter(isToolTimingStep);
		const stageSteps = steps.filter((step) => !isToolTimingStep(step));

		return (
			<div className="bubble-timing-timeline">
				{toolSteps.length > 0 && (
					<>
						<div className="bubble-timing-timeline__section">
							{t('conversation.debug.timing-tools', {defaultValue: 'Tools'})}
						</div>
						{renderTimingRows(toolSteps, totalMillis, "bubble-timing-timeline__row--tool")}
					</>
				)}
				{stageSteps.length > 0 && (
					<>
						<div className="bubble-timing-timeline__section">
							{t('conversation.debug.timing-stages', {defaultValue: 'Pipeline'})}
						</div>
						{renderTimingRows(stageSteps, totalMillis)}
					</>
				)}
			</div>
		);
	}

	const formatTimingTotal = (steps: ITimingStep[]): string => {
		const totalMillis = Math.max(...steps.map((step) => step.startOffsetMillis + step.durationMillis), 1);
		return formatStepDuration(totalMillis);
	}

	const parseTokenUsageBreakdown = (value: string | undefined): ITokenUsageBreakdown | null => {
		if (!value) {
			return null;
		}
		try {
			const parsed = JSON.parse(value);
			if (!parsed || !Array.isArray(parsed.calls)) {
				return null;
			}
			const calls: ITokenUsageCall[] = parsed.calls
				.filter((call: any) => call && typeof call.name === 'string')
				.map((call: any) => ({
					name: call.name,
					inputTokens: Number(call.inputTokens) || 0,
					outputTokens: Number(call.outputTokens) || 0,
					totalTokens: Number(call.totalTokens) || 0,
				}));
			if (calls.length === 0) {
				return null;
			}
			return {
				calls,
				inputTokens: Number(parsed.inputTokens) || calls.reduce((sum, call) => sum + call.inputTokens, 0),
				outputTokens: Number(parsed.outputTokens) || calls.reduce((sum, call) => sum + call.outputTokens, 0),
				totalTokens: Number(parsed.totalTokens) || calls.reduce((sum, call) => sum + call.totalTokens, 0),
			};
		} catch {
			return null;
		}
	}

	const renderTokenUsage = (usage: ITokenUsageBreakdown) => {
		return (
			<div className="bubble-token-usage">
				{usage.calls.map((call, index) => (
					<div className="bubble-token-usage__row" key={`${call.name}-${index}`}>
						<span className="bubble-token-usage__label">{call.name}</span>
						<span className="bubble-token-usage__detail">
							{t('conversation.debug.tokens-call', {
								input: call.inputTokens,
								output: call.outputTokens,
								total: call.totalTokens,
								defaultValue: `in ${call.inputTokens} · out ${call.outputTokens} · ${call.totalTokens}`,
							})}
						</span>
					</div>
				))}
			</div>
		);
	}

	interface IDebugData {
		sqlQuery?: string,
		sqlQueryExplanation?: string,
		sqlQueryFiltering?: string,
		csvData?: string,
		failureReason?: string,
		timingSteps: ITimingStep[],
		tokenUsage: ITokenUsageBreakdown | null,
	}

	const debugData: IDebugData | null = useMemo(() => {
		if (!canViewDebugInfo || conversationMessage.conversationMessageType?.code !== "ASKAIRSPN") {
			return null;
		}

		const sqlQuery = findMetadataValue("SQL_QUERY");
		const sqlQueryExplanation = findMetadataValue("SQL_QUERY_EXPLANATION");
		const sqlQueryFiltering = findMetadataValue("SQL_QUERY_FILTERING");
		const csvData = findMetadataValue("CSV_DATA");
		const failureReason = findMetadataValue("FAILURE_REASON");
		const timingSteps = parseTimingBreakdown(findMetadataValue("TIMING_BREAKDOWN"));
		const tokenUsage = parseTokenUsageBreakdown(findMetadataValue("TOKEN_USAGE_BREAKDOWN"));

		if (!sqlQuery && !sqlQueryExplanation && !sqlQueryFiltering && !csvData && !failureReason && timingSteps.length === 0 && !tokenUsage) {
			return null;
		}
		return {sqlQuery, sqlQueryExplanation, sqlQueryFiltering, csvData, failureReason, timingSteps, tokenUsage};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canViewDebugInfo, conversationMessage]);

	const debugMenuItems: MenuItem[] = useMemo(() => debugData ? [
		{
			label: t('conversation.debug.menu-item'),
			icon: 'pi pi-wrench',
			command: () => setDebugDialogVisible(true),
		},
	] : [], [debugData, t]);

	const handleBubbleContextMenu = (event: React.MouseEvent) => {
		if (!debugData) {
			return; // nothing to show — let the browser's own context menu appear
		}
		event.preventDefault();
		contextMenuRef.current?.show(event);
	}

	const renderDebugDialogContent = (data: IDebugData) => (
		<>
			{data.failureReason && (
				<p><strong>{t('conversation.debug.failure-reason')}</strong> {data.failureReason}</p>
			)}
			{data.sqlQueryExplanation && (
				<p><strong>{t('conversation.debug.explanation')}</strong> {data.sqlQueryExplanation}</p>
			)}
			{data.sqlQueryFiltering && (
				<p><strong>{t('conversation.debug.filtering')}</strong> {data.sqlQueryFiltering}</p>
			)}
			{data.sqlQuery && (
				<>
					<strong>{t('conversation.debug.sql-query')}</strong>
					<pre className="bubble-debug-info-pre">{data.sqlQuery}</pre>
				</>
			)}
			{data.csvData && (
				<>
					<strong>{t('conversation.debug.csv-data')}</strong>
					<pre className="bubble-debug-info-pre">{data.csvData}</pre>
				</>
			)}
			{data.timingSteps.length > 0 && (() => {
				const totalLabel = formatTimingTotal(data.timingSteps);
				return (
					<>
						<div className="bubble-timing-header">
							<strong>{t('conversation.debug.timing', {defaultValue: 'Tijdsverdeling'})}</strong>
							<span className="bubble-timing-header__total">
								{t('conversation.debug.timing-total', {
									duration: totalLabel,
									defaultValue: `Totaal: ${totalLabel}`,
								})}
							</span>
						</div>
						{renderTimingTimeline(data.timingSteps)}
					</>
				);
			})()}
			{data.tokenUsage && (
				<>
					<div className="bubble-timing-header">
						<strong>{t('conversation.debug.tokens', {defaultValue: 'Tokens'})}</strong>
						<span className="bubble-timing-header__total">
							{t('conversation.debug.tokens-total', {
								input: data.tokenUsage.inputTokens,
								output: data.tokenUsage.outputTokens,
								total: data.tokenUsage.totalTokens,
								defaultValue: `Totaal: ${data.tokenUsage.totalTokens} (in ${data.tokenUsage.inputTokens} · out ${data.tokenUsage.outputTokens})`,
							})}
						</span>
					</div>
					{renderTokenUsage(data.tokenUsage)}
				</>
			)}
		</>
	)

	const determinePosition = () => {
		if (type === "ASKAI") {
			if (['ASKAITHNK', 'ASKAIGNRN', 'ASKAIRSPN', 'ASKAIFAIL'].includes(conversationMessage.conversationMessageType.code)) {
				return 'bubble-left' + (conversationMessage.conversationMessageType.code === "ASKAIFAIL" ? ' bubble-error' : '');
			} else if (['ASKAIQSTN'].includes(conversationMessage.conversationMessageType.code)) {
				return 'bubble-right';
			}
		} else if (type === "MESSAGING") {
			if (conversationMessage.createdBy.uuid === user?.uuid) {
				return 'bubble-right';
			} else {
				return 'bubble-left';
			}
		} else if (type === "DATE") {
			return 'bubble-center';
		}
	}

	const displayHeaderFooter = (): boolean => {
		if (type === "ASKAI") {
			if (['ASKAITHNK', 'ASKAIFAIL'].includes(conversationMessage.conversationMessageType.code)) {
				return false;
			} else if (['ASKAIGNRN', 'ASKAIRSPN'].includes(conversationMessage.conversationMessageType.code)) {
				return true;
			}
		} else if (type === "DATE") {
			return false;
		}
		return true;
	}

	const determineAuthor = () => {
		return (<>
			<div className="bubble-author">
				{
					(['ASKAIGNRN', 'ASKAITHNK', 'ASKAIRSPN'].includes(conversationMessage.conversationMessageType.code)) ?
						t('conversation.ask-ai') :
						conversationMessage.createdBy?.name ?? t('conversation.unknown-sender')
				}
			</div>
		</>)

	}

	const canCopyBubble = (): boolean => {
		if (type === "DATE" || conversationMessage.uuid === "SENDING") {
			return false;
		}
		const code = conversationMessage.conversationMessageType?.code;
		if (type === "ASKAI" && !COPYABLE_ASK_AI_CODES.includes(code)) {
			return false;
		}
		return !!htmlToConversationPlainText(conversationMessage.message);
	};

	const copyBubbleText = async () => {
		const text = htmlToConversationPlainText(conversationMessage.message);
		if (!text) {
			return;
		}
		try {
			await navigator.clipboard.writeText(text);
			toast.success(t('conversation.copy.success'));
		} catch {
			toast.error(t('conversation.copy.error'));
		}
	};

	const determineBubbleActions = () => {
		const showCopy = canCopyBubble();
		const showRating = conversationMessage.conversationMessageType?.code === "ASKAIRSPN";
		if (!showCopy && !showRating) {
			return (<><div></div></>);
		}
		return (<>
			<div className="bubble-actions">
				<Tooltip target="[data-pr-tooltip]" showDelay={500} />
				{showCopy && (
					<i className="pi pi-copy"
					   data-pr-tooltip={t('conversation.copy')}
					   onClick={() => { void copyBubbleText(); }} />
				)}
				{showRating && (
					<>
						<i className={retrieveRating() === 1 ? "pi pi-thumbs-up-fill" : "pi pi-thumbs-up"}
						   data-pr-tooltip={t('conversation.react.like')}
						   onClick={() => rateConversationMessage(1)} />
						<i className={retrieveRating() === 0 ? "pi pi-question-circle" : "pi pi-question"}
						   data-pr-tooltip={t('conversation.react.unknown')}
						   onClick={() => rateConversationMessage(0)} />
						<i className={retrieveRating() === -1 ? "pi pi-thumbs-down-fill" : "pi pi-thumbs-down"}
						   data-pr-tooltip={t('conversation.react.dislike')}
						   onClick={() => rateConversationMessage(-1)} />
					</>
				)}
			</div>
		</>);
	}

	const determineMessage = () => {
		if (type === "ASKAI") {
			if (conversationMessage.uuid === "SENDING") {
				return (<>
					<i className="pi pi-spin pi-spinner mr-2"/>
				</>)
			}
			if (conversationMessage.conversationMessageType.code === "ASKAIGNRN") {
				return (<>
					<i className="pi pi-spin pi-spinner mr-2"/>
					<span>{rotatingText}</span>
				</>)
			}
			if (conversationMessage.conversationMessageType.code === "ASKAIFAIL") {
				if (conversationMessage.message) {
					return (<>
						<span>{t('conversation.limit-exceeded')}</span>
					</>)
				} else {
					return (<>
						<span>{t('conversation.generating.exception')}</span>
					</>)
				}
			}
		} else if (type === "DATE") {
			return (<>
				{conversationMessage.messageSentTs}
			</>)
		}
		return (
			<span dangerouslySetInnerHTML={{
				__html: sanitizeConversationHtml(conversationMessage.message, type),
			}} />
		)
	}

	const formatResponseTimeSeconds = (): string | undefined => {
		if (conversationMessage.conversationMessageType?.code !== "ASKAIRSPN") {
			return undefined;
		}
		const responseMillis = Number(findMetadataValue("RESPONSE_MILLIS"));
		if (!Number.isFinite(responseMillis) || responseMillis <= 0) {
			return undefined;
		}
		const seconds = responseMillis / 1000;
		return t('conversation.response-time', {
			seconds: seconds.toFixed(seconds < 10 ? 1 : 0),
			defaultValue: '{{seconds}}s',
		});
	}

	const determineFooter = () => {
		if (conversationMessage.messageSentTs) {
			const responseTime = formatResponseTimeSeconds();
			return (
				<>
					{DateText(conversationMessage.messageSentTs, false, false, groupByDate ? 'HH:mm' : "dd-MM-yyyy HH:mm")}
					{responseTime && <span className="bubble-response-time"> · {responseTime}</span>}
				</>
			)
		}
		return undefined;
	}

	return (
		<>
			<div className={classNames("conversation-bubble", !selectionAllowed ? "no-select" : "")}>
				<div className={classNames(determinePosition())}>
					{displayHeaderFooter() && (
						<div className="bubble-header">
							{determineBubbleActions()}
							{determineAuthor()}
						</div>
					)}

					<div className="bubble-content" onContextMenu={handleBubbleContextMenu}>
						{determineMessage()}
					</div>

					{displayHeaderFooter() && (
						<div className="bubble-footer">
							{determineFooter()}
						</div>
					)}
				</div>

				{debugData && (
					<>
						<ContextMenu model={debugMenuItems} ref={contextMenuRef} />
						<AppDialog
							header={t('conversation.debug.title')}
							visible={debugDialogVisible}
							onHide={() => setDebugDialogVisible(false)}
							style={{width: '640px'}}
							className="bubble-debug-dialog"
						>
							{renderDebugDialogContent(debugData)}
						</AppDialog>
					</>
				)}
			</div>
		</>
	);
};

export default ConversationBubble